"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentDoctor } from "../../authDoctor/redux/thunks/doctorsThunks";
import { fetchRoomsByStatus } from "../redux/thunks/room.thunks";
import { fetchUserById } from "../../Profile/redux/thunks/userThunks";
import { selectAllRooms } from "../redux/selectors/room.selectors";
import { createSelector } from "@reduxjs/toolkit";
import { fetchConfirmedRendezvous } from "../../appointments/redux/thunks/rendezvousThunks";
import { selectRdvConfirmed } from "../../appointments/redux/selectors/rendezvousSelectors";
import { fetchLocalPatients } from "../redux/thunks/localPatient.thunks";
import { selectAllLocalPatients } from "../redux/selectors/localPatient.selectors";
import useConsultations from "../../consultation/hooks/useConsultations";
import { axiosSousProfile } from "../../api/axios";
import { SOUS_PROFILE_PATHS } from "../../api/endpoints";



const computeAgeFromBirthdate = (birthdate) => {
  if (!birthdate) return "";
  const d = new Date(birthdate);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age > 0 ? age : "";
};

function isToday(isoString) {
  if (!isoString) return false;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth()    === now.getMonth()    &&
    d.getDate()     === now.getDate()
  );
}

export function filterTodayConfirmedAppointments(items = []) {
  return (Array.isArray(items) ? items : []).filter((item) => {
    const status = String(item?.status || "").toLowerCase();
    if (!['confirmed', 'rescheduled'].includes(status)) return false;
    const dateValue = item?.startTime || item?.date || item?.createdAt || item?.updatedAt;
    return isToday(dateValue);
  });
}

function getAppointmentDay(room, rdv) {
  return rdv?.startTime || room?.createdAt || room?.updatedAt || room?.startTime || null;
}

function formatTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const unwrapPayload = (payload) => payload?.data ?? payload;

const getSubprofileId = (item, patientId) => {
  const value =
    item?.subprofileId ??
    item?.subProfileId ??
    item?.subprofile?.id ??
    item?.subprofile?._id ??
    item?.subProfile?.id ??
    (item?.effectiveProfileId && item.effectiveProfileId !== patientId
      ? item.effectiveProfileId
      : null);
  const id = String(value ?? "").trim();
  return id && id !== String(patientId ?? "").trim() ? id : null;
};

const getProfileName = (profile) =>
  profile?.fullName ||
  profile?.name ||
  [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") ||
  [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
  "";

const getProfileBirthdate = (profile) =>
  profile?.birthdate ||
  profile?.dateOfBirth ||
  profile?.dob ||
  profile?.birthDate ||
  null;

export function recomputeInConsultation(patients) {
  const waitingCol = patients.filter((p) => p.status === "waiting");
  const firstId    = waitingCol[0]?.id ?? null;

  return patients.map((p) => {
    if (p.status !== "waiting") return { ...p, inConsultation: false };
    return { ...p, inConsultation: p.id === firstId };
  });
}

/* ─── Hook ───────────────────────────────────────────────────────────────── */

export default function useDoctorConfirmedTodayForWaitingRoom(options = {}) {
  const {
    disableRemote = false,
    localPatientsData = [],
    doctorIdOverride = null,
  } = options;
  const dispatch = useDispatch();

  const currentDoctor = useSelector((state) => state.doctor.user);
  const usersById     = useSelector((state) => state.user.usersById || {});
  const confirmedRdvs = useSelector(selectRdvConfirmed);
  const todayConfirmedRdvs = useMemo(
    () => filterTodayConfirmedAppointments(confirmedRdvs),
    [confirmedRdvs]
  );
  const localPatients = useSelector(selectAllLocalPatients);

  const {
  doctorConsultations,
  getByDoctorId,
} = useConsultations();

  const doctorId =
    doctorIdOverride ||
    currentDoctor?.doctorId ||
    currentDoctor?.pgId     ||
    currentDoctor?.id       ||
    null;

  /* ── Selector mémoïsé : 4 statuts actifs du doctor ── */
  const selectActiveDoctorRooms = useMemo(
    () =>
      createSelector(selectAllRooms, (rooms) =>
        rooms.filter(
          (r) =>
            r?.doctorId === doctorId &&
            ["confirmed", "waiting", "in_consultation", "completed"].includes(r?.status) 
        )
      ),
    [doctorId]
  );

  const activeDoctorRooms = useSelector(selectActiveDoctorRooms);


  /* ── Map appointmentId → RDV pour jointure ── */
 const rdvByAppointmentId = useMemo(() => {
  const map = {};
  for (const rdv of todayConfirmedRdvs) {
    const key = rdv?.id ?? rdv?._id;
    if (key != null) map[String(key)] = rdv;
  }
  return map;
}, [todayConfirmedRdvs]);

const consultationByAppointmentId = useMemo(() => {
  const map = {};

  for (const consultation of doctorConsultations || []) {
    const key =
      consultation?.appointmentId ||
      consultation?.rendezvousId ||
      consultation?.rdvId;

    if (key) {
      map[String(key)] = consultation;
    }
  }

  return map;
}, [doctorConsultations]);

  const [patients, setPatients] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [subprofilesById, setSubprofilesById] = useState({});
  const loadedDoctorIdRef = useRef(null);
  const loadingDoctorIdRef = useRef(null);
  const fetchingUsersRef = useRef(new Set());

  const localPatientsById = useMemo(() => {
    return (localPatients || []).reduce((acc, patient) => {
      if (patient?.id) acc[String(patient.id)] = patient;
      return acc;
    }, {});
  }, [localPatients]);

  const subprofileIds = useMemo(() => {
    const ids = new Set();
    for (const room of activeDoctorRooms || []) {
      const rdv = rdvByAppointmentId[String(room?.appointmentId)];
      const patientId = room?.patientId ?? rdv?.patientId;
      const id = getSubprofileId({ ...rdv, ...room }, patientId);
      if (id) ids.add(id);
    }
    for (const rdv of todayConfirmedRdvs) {
      const id = getSubprofileId(rdv, rdv?.patientId);
      if (id) ids.add(id);
    }
    return Array.from(ids);
  }, [activeDoctorRooms, rdvByAppointmentId, todayConfirmedRdvs]);

  useEffect(() => {
    if (disableRemote || !subprofileIds.length) return;
    const missingIds = subprofileIds.filter((id) => !(id in subprofilesById));
    if (!missingIds.length) return;

    let cancelled = false;
    Promise.all(
      missingIds.map(async (id) => {
        try {
          const { data } = await axiosSousProfile.get(SOUS_PROFILE_PATHS.GET_BY_ID(id), {
            withCredentials: true,
          });
          return [id, unwrapPayload(data)];
        } catch {
          return [id, null];
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setSubprofilesById((previous) => {
        const next = { ...previous };
        entries.forEach(([id, profile]) => {
          next[id] = profile;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [disableRemote, subprofileIds, subprofilesById]);

  /* ── 1) Charger le doctor si absent ── */
  useEffect(() => {
    if (disableRemote) return;
    if (doctorIdOverride) return;
    if (!currentDoctor) dispatch(getCurrentDoctor());
  }, [dispatch, currentDoctor, disableRemote, doctorIdOverride]);

  /* ── 2) Charger rooms (4 statuts) + RDV confirmés today ── */
  useEffect(() => {
    if (disableRemote) return;
    if (!doctorId) return;

    if (
      loadingDoctorIdRef.current === doctorId ||
      loadedDoctorIdRef.current === doctorId
    ) {
      return;
    }

    loadingDoctorIdRef.current = doctorId;

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      dispatch(fetchLocalPatients({ activeOnly: true })).unwrap(),
      dispatch(fetchRoomsByStatus({ status: "confirmed"       })).unwrap(),
      dispatch(fetchRoomsByStatus({ status: "waiting"         })).unwrap(),
      dispatch(fetchRoomsByStatus({ status: "in_consultation" })).unwrap(),
      dispatch(fetchRoomsByStatus({ status: "completed"       })).unwrap(), // ✅
      dispatch(fetchConfirmedRendezvous({ userId: doctorId, period: "today" })).unwrap(),
      getByDoctorId(doctorId).unwrap(),
    ])
      .then(() => {
        loadedDoctorIdRef.current = doctorId;
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.message || "Failed to load waiting room data");
      })
      .finally(() => {
        if (loadingDoctorIdRef.current === doctorId) {
          loadingDoctorIdRef.current = null;
        }
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [dispatch, doctorId, getByDoctorId, disableRemote]);

  useEffect(() => {
    if (!disableRemote) return;

    const normalized = (localPatientsData || []).map((patient, index) => ({
      id: patient.id || `local-${index + 1}`,
      roomId: patient.id || `local-${index + 1}`,
      appointmentId: patient.appointmentId || null,
      patientId: patient.patientId || patient.id || `local-${index + 1}`,
      doctorId: null,
      consultationId: patient.consultationId || null,
      paymentStatus: patient.paymentStatus || "unpaid",
      amount: patient.amount || "",
      paymentType: patient.paymentType || null,
      paymentMethod: patient.paymentMethod || null,
      consultationPrice: Number(patient.consultationPrice || patient.amount || 0),
      medicalActsTotal: Number(patient.medicalActsTotal || 0),
      name: patient.name || "Patient",
      age: patient.age || "",
      source: patient.source || "local",
      identifier: patient.identifier || "",
      avatarUrl: patient.avatarUrl || null,
      time: patient.time || "",
      reason: patient.reason || "",
      status: patient.status || "confirmed",
      backendStatus: patient.status || "confirmed",
      inConsultation: Boolean(patient.inConsultation),
      waitingTime: patient.waitingTime || null,
      startTime: patient.startTime || null,
    }));

    setPatients(recomputeInConsultation(normalized));
    setLoading(false);
    setError(null);
  }, [disableRemote, localPatientsData]);

  /* ── 3) Transformer rooms → shape "patient" avec jointure RDV ── */
  useEffect(() => {
    if (disableRemote) return;

    const todayRooms = (activeDoctorRooms || []).filter((r) => {
      const rdv = rdvByAppointmentId[String(r.appointmentId)] ?? null;
      // A room linked to an appointment must follow the appointment date.
      // Falling back to room.createdAt would show future appointments today.
      if (r?.appointmentId != null) {
        return Boolean(rdv) && isToday(rdv.startTime || rdv.date);
      }
      return isToday(getAppointmentDay(r, rdv));
    });

    const roomAppointmentIds = new Set(
      todayRooms
        .map((room) => room.appointmentId)
        .filter((appointmentId) => appointmentId != null)
        .map(String),
    );

    // A confirmed appointment may not have a room until the patient arrives.
    // Keep it visible in the confirmed column until the room is created.
    const todayAppointmentsWithoutRoom = todayConfirmedRdvs.filter(
      (rdv) =>
        rdv?.id != null && !roomAppointmentIds.has(String(rdv.id)),
    );

    const roomsWithFallbackAppointments = [
      ...todayRooms,
      ...todayAppointmentsWithoutRoom.map((rdv) => ({
        appointmentId: rdv.id,
        patientId: rdv.patientId,
        subprofileId: rdv.subprofileId || rdv.subProfileId || null,
        effectiveProfileId:
          rdv.effectiveProfileId || rdv.subprofileId || rdv.patientId,
        doctorId: rdv.medecinId || rdv.doctorId || doctorId,
        status: "confirmed",
        roomId: `appointment-${rdv.id}`,
      })),
    ];

    if (!roomsWithFallbackAppointments.length) {
      setPatients([]);
      return;
    }

    const basePatients = roomsWithFallbackAppointments.map((room) => {
      const rdv = rdvByAppointmentId[String(room.appointmentId)] ?? null;
      const principalPatientId = room.patientId || rdv?.patientId;
      const subprofileId =
        getSubprofileId(room, principalPatientId) ||
        getSubprofileId(rdv, principalPatientId);
      const subprofile =
        rdv?.subprofile ||
        rdv?.subProfile ||
        room?.subprofile ||
        room?.subProfile ||
        (subprofileId ? subprofilesById[subprofileId] : null);
      const effectiveProfileId =
        subprofileId || room.effectiveProfileId || rdv?.effectiveProfileId || principalPatientId;
      const u      =
        usersById[subprofileId] ||
        usersById[effectiveProfileId] ||
        usersById[principalPatientId];
      const local  = localPatientsById[String(principalPatientId)];
      const roomId = room.roomId ?? room.id;

      const patientInfo = subprofile || u || local || null;
      const consultation = consultationByAppointmentId[String(room.appointmentId)] ?? null;
      const waitingRoomPayment = room.waitingRoomPayment || null;
      const medicalActsTotal = Array.isArray(consultation?.medicalActs)
        ? consultation.medicalActs.reduce((sum, act) => {
            const key = Number(act?.key);
            const coefficient = Number.isFinite(key) && key > 0 ? key : 1;
            return sum + coefficient * Number(act?.cotation ?? act?.price ?? 0);
          }, 0)
        : Number(consultation?.medicalActsTotal || 0);

      const fullName =
        getProfileName(patientInfo) ||
        getProfileName(rdv) ||
        rdv?.patientName ||
        "Patient";

      const birthdate = getProfileBirthdate(patientInfo);
      const age = birthdate
        ? computeAgeFromBirthdate(birthdate)
        : patientInfo?.age || "";

      const time   = rdv?.startTime ? formatTime(rdv.startTime) : "";
      const reason = rdv?.reason || rdv?.motif || "";

      const source = subprofile ? "Sub-profile" : local ? "local" : "Medilink";
      const identifier =
        (subprofile ? subprofile?.identifier || subprofile?.id : null) ||
        u?.identifier ||
        u?.patientIdentifier ||
        rdv?.patientIdentifier ||
        rdv?.identifier ||
        room?.patientIdentifier ||
        "";
      const avatarUrl =
        subprofile?.avatarUrl ||
        subprofile?.avatar ||
        subprofile?.photo ||
        u?.avatarUrl ||
        u?.avatar ||
        u?.photo ||
        local?.avatarUrl ||
        local?.avatar ||
        null;

      // ✅ mapping des statuts frontend
      const frontStatus =
        room.status === "in_consultation" ? "waiting" : room.status;
      // confirmed      → "confirmed"
      // waiting        → "waiting"
      // in_consultation→ "waiting"  (affiché dans colonne waiting)
      // completed      → "completed" ✅

      return {
        id: roomId,
        roomId,
        appointmentId: room.appointmentId,
        patientId: principalPatientId,
        subprofileId,
        effectiveProfileId,
        doctorId: room.doctorId,

        consultationId: consultation?.id || null,
        waitingRoomPayment,
        paymentStatus: waitingRoomPayment?.paymentStatus || consultation?.paymentStatus || "unpaid",
        amount: waitingRoomPayment?.amount || consultation?.amount || "",
        paymentType: waitingRoomPayment?.paymentType || consultation?.paymentType || null,
        paymentMethod: waitingRoomPayment?.paymentMethod || consultation?.paymentMethod || null,
        paymentHistory: waitingRoomPayment?.paymentHistory || consultation?.paymentHistory || [],
        consultationPrice: Number(waitingRoomPayment?.consultationPrice ?? consultation?.consultationPrice ?? consultation?.amount ?? 0),
        medicalActsTotal: Number(waitingRoomPayment?.medicalActsTotal ?? medicalActsTotal),

        name: fullName,
        age,
        source,
        identifier,
        avatarUrl,
        time,
        reason,
        status: frontStatus,
        backendStatus: room.status,
        inConsultation: room.status === "in_consultation",
        waitingTime: null,
        startTime: rdv?.startTime ?? null,
      };
    });

    setPatients(recomputeInConsultation(basePatients));
  }, [
    activeDoctorRooms,
    usersById,
    rdvByAppointmentId,
    consultationByAppointmentId,
    todayConfirmedRdvs,
    doctorId,
    localPatientsById,
    subprofilesById,
    disableRemote,
  ]);

  /* ── 4) Précharger patients manquants ── */
  useEffect(() => {
    if (disableRemote) return;
    const missing = patients
      .map((p) => p.patientId)
      .filter(Boolean)
      .filter((id) => !localPatientsById[String(id)])
      .filter((id) => !usersById[id]);

    Array.from(new Set(missing)).forEach((userId) => {
      if (fetchingUsersRef.current.has(userId)) return;
      fetchingUsersRef.current.add(userId);
      dispatch(fetchUserById(userId)).finally(() => {
        fetchingUsersRef.current.delete(userId);
      });
    });
  }, [patients, usersById, localPatientsById, dispatch, disableRemote]);

  return { patients, setPatients, loading, error };
}